import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Badge,
  Spinner,
  Table
} from 'react-bootstrap';
import { http } from '../../features/jobs/api/http';
import { fetchJob } from '../../features/jobs/api/jobsApi';
import { CABLE_NAMES_NO_ROPE, CABLE_COLORS } from '../../data/cableProducts.js';
import ProductColorDot from '../../components/common/ProductColorDot';
import { API } from '../../config/api';
import { PHASE_LABEL } from './ProjectJobsMonitor';
import { formatProductLabel } from '../../helpers/productLabel';
// What the fork will really do to each drum, run here so it is visible while a
// drum can still be unticked — the same two rules `forkProduct` applies.
import {
  transformSkips,
  skipReasonText,
  drumsOfJob,
  byPhase,
  lockedPhase,
  blockedReason,
  planTransform,
  problemsOf
} from '../../helpers/transformDrums';

const toNum = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt = n => Number(n || 0).toLocaleString();

const colorFromKey = key => {
  const parts = String(key || '').split('|');
  const up = new Set(CABLE_COLORS.map(c => c.toUpperCase()));
  return parts.find(seg => up.has(String(seg).trim().toUpperCase())) || '';
};

// Phases that are parallel per conductor: the branch holds N drums of the SAME
// length, one per conductor colour, and the product length is one drum's draw —
// not their sum. This is also the case where a partial selection quietly
// produces an unbuildable target (see the warning below).
const PARALLEL_PHASES = new Set(['conductor_isolation', 'unwinding']);

// TRANSFORM — re-plan a launched product into a different one.
//
// Three operations behind one screen, picked from the state of the selected
// drums (the wizard does not ask, because the answer is never the planner's to
// give):
//   total                     → convert-product      (in place, same job)
//   partial, drums produced   → fork-product         (cut the drums, new job)
//   partial, drums still queued → fork-product-merge (merge-execution pair)
/**
 * The three ways a split can sit on the machine.
 *
 * Module level because the per-phase table and the "set all" control both read
 * it, and because this wording is the only place the difference is explained.
 */
const LAYOUT_OPTIONS = [
  {
    key: 'merge',
    title: 'Merge execution (default)',
    body: (src, tgt) =>
      `One bobbin per colour, shared. ${src} runs its metres and the line rolls ` +
      `straight over onto ${tgt} on the same drum — no drum change between ` +
      `them. Fewest bobbins, fewest stops.`
  },
  {
    key: 'byColor',
    title: 'Own drums, grouped by colour',
    body: (src, tgt) =>
      `Its own bobbins, each beside the same-colour drum it came off: ${src} ` +
      `blue, ${tgt} blue, then brown, then green-yellow. A drum change between ` +
      `each pair, but the line never changes COLOUR more than once per colour.`
  },
  {
    key: 'series',
    title: 'Own drums, one product then the other',
    body: (src, tgt) =>
      `As if they were two launches: every drum of ${src} first, then every ` +
      `drum of ${tgt}, as one block immediately below — whatever the number ` +
      `of drums on either side.`
  }
];

const TransformWizzard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const sourceJobId = location?.state?.sourceJobId || '';
  const navRows = Array.isArray(location?.state?.rows) ? location.state.rows : [];
  const returnTo =
    location?.state?.returnTo || '/apps/project-management/project-monitor';

  const [srcJob, setSrcJob] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [submitErr, setSubmitErr] = useState('');
  const [busy, setBusy] = useState(false);
  // set when the transform succeeded but the backend's consistency audit found
  // something — the hand-off to plan-next-phase waits behind it
  const [resultWarnings, setResultWarnings] = useState([]);
  const [pendingRows, setPendingRows] = useState(null);

  // ── WHICH DRUMS THIS SCREEN CAN SEE ──────────────────────────
  //
  // Two sources, and a toggle between them. The wizard used to have only the
  // first: whatever Project Token State had handed over in `location.state`.
  // That is the right DEFAULT — the planner has just picked them — but it left
  // the screen unable to answer "what else is on this product", so transforming
  // a rope meant going back and starting again.
  //
  // Asked for 2026-10-30: "la produsul selectat trebuie sa am un toggle, ceva
  // de genu bring all products and phases, si asta trebuie sa intoarca toti
  // tokeni care sunt disponibili pe toate fazele lansate in executie, in
  // pending, sau intr-o forma sau alta din plan ahead."
  //
  // `drumsOfJob` reads the job document this screen already loads, so there is
  // no second endpoint and no second definition of what a drum is.
  const [allPhases, setAllPhases] = useState(false);

  const handedOver = useMemo(() => {
    const seen = new Set();
    return navRows
      .filter(r => r?.tokenId && !seen.has(r.tokenId) && seen.add(r.tokenId))
      .map(r => String(r.tokenId));
  }, [navRows]);

  const jobDrums = useMemo(() => drumsOfJob(srcJob), [srcJob]);

  // `available` / `planned` / `produced` / `isPending` are kept as aliases of
  // the helper's own field names, so the rest of this screen reads unchanged.
  const allDrums = useMemo(() => {
    const handed = new Set(handedOver);
    return jobDrums
      .filter(d => allPhases || handed.has(d.tokenId))
      .map(d => ({
        ...d,
        available: d.availableLength,
        planned: d.plannedLength,
        produced: d.producedLength,
        isPending: d.kind === 'pending',
        wasHandedOver: handed.has(d.tokenId)
      }));
  }, [jobDrums, handedOver, allPhases]);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Only the drums handed over start ticked, and only those that can actually
  // give metres. Ticking everything the toggle reveals would be TOTAL by
  // another route.
  useEffect(() => {
    const handed = new Set(handedOver);
    setSelectedIds(
      new Set(
        jobDrums
          .filter(d => handed.has(d.tokenId) && d.maxTake > 0)
          .map(d => d.tokenId)
      )
    );
  }, [jobDrums, handedOver]);

  // ── HOW MUCH LEAVES EACH DRUM ───────────────────────────────
  //
  // One figure per drum, typed freely. Asked for: "trebuie sa avem per token cu
  // cat alegem sa pastram si sa convertim din el […] orice ar fi sa nu-mi
  // modifice tot lotul complet, doar cele selectate."
  //
  // An empty box means ZERO, never "all of it" — that direction is what TOTAL
  // was, and `planTransform` enforces it.
  const [takes, setTakes] = useState({});
  const setTake = (tokenId, v) =>
    setTakes(prev => ({ ...prev, [tokenId]: v }));

  // A convenience for the common case where every core gives the same length.
  // Deliberately NOT the source of truth: it writes into the per-drum boxes and
  // then has no further say, so there is no second figure that could disagree
  // with them. That is how TOTAL managed to ignore the selection.
  // HOW THE TWO HALVES SIT ON THE MACHINE, when the drums are still queued.
  //
  // `merge` is the default, as asked: one bobbin, the fork's share rolled over
  // onto it. The other two give the fork its own drums and differ only in where
  // they land in the queue. Only offered on the merge path — a drum that has
  // already been MADE is cut, and there is nothing left to lay out.
  // ONE CHOICE PER PHASE, with a default for the ones nobody touched.
  //
  // A split reaches every phase from the root up to the branch — isolation
  // AND rope AND whatever sits above them — and they do not have to be laid
  // out the same way. Asked for 2026-10-30: *"as vrea sa am putere de decizie
  // pentru toate fazele astea … daca am 5 phasez lansate sa am pentru toate un
  // scenariu. poate vreau pentru toate la fel."*
  //
  // `layoutAll` is the one-click answer to "the same everywhere"; `layouts`
  // holds the per-phase overrides. Both are posted: the endpoint reads
  // `layouts[phase]` and falls back to `layout`.
  const [layoutAll, setLayoutAll] = useState('merge');
  const [layouts, setLayouts] = useState({});
  const layoutOf = ph => layouts[ph] || layoutAll;

  const [setAllValue, setSetAllValue] = useState('');
  const applySetAll = () => {
    const v = toNum(setAllValue);
    setTakes(prev => {
      const next = { ...prev };
      for (const d of allDrums) {
        if (!selectedIds.has(d.tokenId)) continue;
        next[d.tokenId] = String(Math.max(0, Math.min(v, d.maxTake)));
      }
      return next;
    });
  };

  // THE PHASE THE SELECTION IS LOCKED TO. Null while nothing is ticked, which
  // is what unlocks the list again. One phase at a time, by decision: a later
  // phase consumes the drums of an earlier one, so moving both would move the
  // same metres twice.
  const locked = useMemo(
    () => lockedPhase(allDrums, selectedIds),
    [allDrums, selectedIds]
  );

  // THE PLAN, derived once and handed to the preview, the warnings AND the
  // submit — so what the screen shows and what the backend is asked to do
  // cannot drift apart. That drift is exactly what TOTAL was.
  const plan = useMemo(
    () => planTransform({ drums: allDrums, selectedIds, takes }),
    [allDrums, selectedIds, takes]
  );
  const problems = useMemo(
    () => problemsOf(plan, { sourceLength: toNum(srcJob?.plannedLength) }),
    [plan, srcJob]
  );
  const blocking = problems.filter(p => p.blocking);

  const branchDrums = useMemo(
    () => allDrums.filter(d => selectedIds.has(d.tokenId)),
    [allDrums, selectedIds]
  );

  const toggle = tokenId =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(tokenId)) next.delete(tokenId);
      else next.add(tokenId);
      return next;
    });

  const branchPhase = branchDrums[0]?.phase || allDrums[0]?.phase || '';

  // THE PHASES THE SPLIT REACHES: the phasePlan from the root up to and
  // including the branch. Derived exactly as `forkProductMerge` derives its own
  // `sharedPhases` — `planNames.slice(0, indexOf(branchPhase) + 1)` — so the
  // screen offers a choice for each phase the endpoint will actually lay out,
  // and never for one it will not touch.
  // WHICH SHARED PHASES STILL HAVE A DRUM TO LAY OUT.
  //
  // The endpoint walks every shared phase but only splices a queue entry for a
  // PENDING task — a drum already made is cut, not re-planned, and a DONE one
  // must never re-enter the queue. So forking at rope on a lot whose isolation
  // is finished leaves the isolation row with nothing to decide, and saying so
  // beats offering a choice that changes nothing.
  const pendingPhases = useMemo(
    () => new Set(jobDrums.filter(d => d.kind === 'pending').map(d => d.phase)),
    [jobDrums]
  );

  const sharedPhases = useMemo(() => {
    const names = (srcJob?.phasePlan || [])
      .map(pp => pp?.name || pp?.phase)
      .filter(Boolean);
    const i = names.indexOf(branchPhase);
    return i < 0 ? [] : names.slice(0, i + 1);
  }, [srcJob, branchPhase]);
  const mixedPhase = branchDrums.some(d => d.phase !== branchPhase);
  const branchIsPending =
    branchDrums.length > 0 && branchDrums.every(d => d.isPending);
  // Parallel branch drums share the product length → the forkable product
  // length is the MIN forkable across them (planned for pending, else avail).
  const maxFork = branchDrums.length
    ? Math.min(...branchDrums.map(d => (d.isPending ? d.planned : d.available) || 0))
    : 0;

  // TOTAL IS GONE (2026-10-30, by decision). It posted `convert-product` with
  // the job and the target and nothing else, so the drum ticks reached it in no
  // form at all: it rewrote every task of the lot, DONE ones included. "am
  // patit in trecut sa imi converteasca si produse in faza de done ca executie,
  // cand am selectat din greseala TOTAL."
  //
  // The backend endpoint stays — eng-mobile posts to it directly — this screen
  // simply no longer offers a button that ignores the selection.
  const [target, setTarget] = useState({
    name: '',
    numberOfConductors: '',
    section: '',
    color: ''
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!sourceJobId) {
        setLoadErr('Open this from Project Token State: select produced drums of one phase → “Transform / Fork”.');
        return;
      }
      try {
        const job = await fetchJob(sourceJobId);
        if (!alive) return;
        setSrcJob(job);
        setTarget(t => ({
          ...t,
          name: job?.name ?? t.name,
          numberOfConductors: job?.numberOfConductors ?? t.numberOfConductors,
          section: job?.section ?? t.section,
          color: colorFromKey(job?.source?.launch?.productKey) || t.color
        }));
      } catch (e) {
        if (alive) setLoadErr(e?.message || String(e));
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceJobId]);

  const srcColor = colorFromKey(srcJob?.source?.launch?.productKey);
  const isChange =
    String(target.name).trim().toUpperCase() !== String(srcJob?.name || '').toUpperCase() ||
    toNum(target.numberOfConductors) !== toNum(srcJob?.numberOfConductors) ||
    toNum(target.section) !== toNum(srcJob?.section) ||
    String(target.color).trim().toUpperCase() !== String(srcColor).toUpperCase();

  const targetValid =
    String(target.name).trim() &&
    String(target.color).trim() &&
    toNum(target.numberOfConductors) > 0 &&
    toNum(target.section) > 0;

  // ── the target recipe, resolved live ───────────────────────────────────────
  // "Which product does this become" was a question the screen never answered:
  // it showed four form fields and nothing about what they add up to. Resolving
  // the standard as soon as the four are set means the phase chain, the
  // conductor colours and — crucially — how many conductors the target needs
  // are on screen BEFORE submitting, not discovered afterwards.
  const [targetStd, setTargetStd] = useState(null);
  const [stdErr, setStdErr] = useState('');
  const [stdBusy, setStdBusy] = useState(false);

  useEffect(() => {
    if (!targetValid || !srcJob) {
      setTargetStd(null);
      setStdErr('');
      return;
    }
    let alive = true;
    const t = setTimeout(async () => {
      setStdBusy(true);
      setStdErr('');
      try {
        const std = await resolveTarget();
        if (alive) setTargetStd(std);
      } catch (e) {
        if (alive) {
          setTargetStd(null);
          setStdErr(e?.message || String(e));
        }
      } finally {
        if (alive) setStdBusy(false);
      }
    }, 350);
    return () => {
      alive = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.name, target.numberOfConductors, target.section, target.color, targetValid, srcJob]);

  // The conductor colours the target recipe expects at the branch phase — the
  // list the selection below is measured against.
  const targetBranchColors = useMemo(() => {
    const ph = (targetStd?.phases || []).find(
      p => (p.name || p.phase) === branchPhase
    );
    if (!ph || !Array.isArray(ph.conductors)) return null;
    return ph.conductors.map(c => c.isolation?.color).filter(Boolean);
  }, [targetStd, branchPhase]);

  // The heart of problem 1: forking a SUBSET of parallel conductors produces a
  // job that owns one drum but whose recipe wants N, so the rope can never be
  // planned and there is no way back. The operation is still allowed — it is
  // occasionally what you want — but it is no longer silent.
  const parallelBranch = PARALLEL_PHASES.has(branchPhase);
  const neededCount = targetBranchColors
    ? targetBranchColors.length
    : toNum(target.numberOfConductors);
  const incompleteSelection =
    parallelBranch &&
    neededCount > 0 &&
    branchDrums.length > 0 &&
    branchDrums.length < neededCount;

  const missingColors = useMemo(() => {
    if (!incompleteSelection || !targetBranchColors) return [];
    const have = new Set(
      branchDrums.map(d => String(d.color || '').toUpperCase())
    );
    return targetBranchColors.filter(c => !have.has(String(c).toUpperCase()));
  }, [incompleteSelection, targetBranchColors, branchDrums]);

  // ── the orders this job owes ───────────────────────────────────────────────
  // A transform changes what the product IS or how much of it exists, so
  // anything already promised has to be re-decided. Nothing is assumed: every
  // line defaults to "leave it", and the planner says what happens to each.
  const [allocCtx, setAllocCtx] = useState(null);
  const [allocErr, setAllocErr] = useState('');
  const [allocBusy, setAllocBusy] = useState(false);
  const [decisions, setDecisions] = useState({});

  useEffect(() => {
    if (!sourceJobId) return undefined;
    let alive = true;
    (async () => {
      setAllocBusy(true);
      setAllocErr('');
      try {
        const { data } = await http.get(
          `/api/redis/jobs/${sourceJobId}/allocation-context`
        );
        if (!alive) return;
        setAllocCtx(data);
        const seed = {};
        for (const l of data?.lines || []) {
          // start from what the job promises today, per delivery item
          const keep = {};
          for (const it of l.deliveryItems || []) keep[it.itemId] = toNum(it.reserved);
          seed[`${l.orderId}::${l.lineId}`] = {
            action: 'KEEP',
            rewriteLine: true,
            keep,
            reduceMeters: 0
          };
        }
        setDecisions(seed);
      } catch (e) {
        if (alive) setAllocErr(e?.response?.data?.error || e?.message || String(e));
      } finally {
        if (alive) setAllocBusy(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [sourceJobId]);

  const allocLines = allocCtx?.lines || [];
  const setDecision = (key, patch) =>
    setDecisions(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  // A fork NEVER carries the allocation over: the new product is a different
  // cable and cannot fulfil a line that asked for the old one, so it starts as
  // stock. What the wizard decides is how much of its promise the SOURCE keeps,
  // now that it can make less.
  const allocationDecisions = useMemo(() => {
    const out = [];
    for (const l of allocLines) {
      const k = `${l.orderId}::${l.lineId}`;
      const d = decisions[k];
      if (!d) continue;

      // Itemised promise → decide it in units (which drums, which coils).
      if (toNum(l.reservedUnits) > 0) {
        const keep = d.keep || {};
        const changed = (l.deliveryItems || []).some(
          it => toNum(keep[it.itemId]) !== toNum(it.reserved)
        );
        if (changed) {
          out.push({
            orderId: l.orderId,
            lineId: l.lineId,
            action: 'TRIM',
            keepReservation: keep
          });
        }
        continue;
      }

      // No itemised promise (the split is still decided at the final phase) —
      // metres are the only statement there is.
      const m = toNum(d.reduceMeters);
      if (m > 0) {
        out.push({
          orderId: l.orderId,
          lineId: l.lineId,
          action: 'REDUCE',
          meters: m
        });
      }
    }
    return out;
  }, [allocLines, decisions]);

  // After a partial fork the source can only make what is left behind, and
  // everything it still promises has to fit inside that. Leaving it over-
  // promised is what made the order read 20,000 / 20,000 when only half of it
  // could still be produced.
  // `plan.buildable` replaces the single typed fork length everywhere: for a
  // parallel phase it is the SHORTEST colour (a cable is as long as its
  // shortest core), otherwise the sum of what the drums give.
  const forkLength = plan.buildable;
  const capacityAfter = Math.max(
    0,
    toNum(srcJob?.plannedLength) - toNum(forkLength)
  );

  const promisedAfter = useMemo(() => {
    let total = 0;
    for (const l of allocLines) {
      const d = decisions[`${l.orderId}::${l.lineId}`] || {};
      if (toNum(l.reservedUnits) > 0) {
        for (const it of l.deliveryItems || []) {
          total += toNum((d.keep || {})[it.itemId]) * toNum(it.lengthPerUnit);
        }
      } else {
        total += Math.max(
          0,
          toNum(l.allocatedFromThisJob) - toNum(d.reduceMeters)
        );
      }
    }
    return total;
  }, [allocLines, decisions]);

  const overPromised = Math.max(0, promisedAfter - capacityAfter);

  // Scale every promise down to what the source can still deliver. Floor per
  // item, so it can only ever land under the cap, never over.
  const autoTrim = () => {
    const ratio = promisedAfter > 0 ? Math.min(1, capacityAfter / promisedAfter) : 1;
    setDecisions(prev => {
      const next = { ...prev };
      for (const l of allocLines) {
        const k = `${l.orderId}::${l.lineId}`;
        const d = { ...(next[k] || {}) };
        if (toNum(l.reservedUnits) > 0) {
          const keep = {};
          for (const it of l.deliveryItems || []) {
            keep[it.itemId] = Math.floor(toNum((d.keep || {})[it.itemId]) * ratio);
          }
          d.keep = keep;
        } else {
          const owed = toNum(l.allocatedFromThisJob);
          d.reduceMeters = Math.max(0, Math.round(owed - owed * ratio));
        }
        next[k] = d;
      }
      return next;
    });
  };


  // ── which ticked drums the fork is going to leave behind ──
  //
  // The same two rules `forkProduct` applies, run here so they are visible while
  // a drum can still be unticked. A drum already planned into a later phase is
  // SKIPPED — correctly, its metres are promised — but the only sign of it used
  // to be the audit afterwards: "token K-a8eb254f: fork 15000 > effective 0".
  //
  // Total converts in place and skips nothing, so this applies to Partial only.
  const skips = useMemo(() => {
    if (!srcJob) return [];
    // Per drum now, not one figure for all of them — a drum with 1 000 m free
    // is only "spoken for" if the figure typed against IT exceeds that.
    return transformSkips({
      job: srcJob,
      tokenIds: plan.rows.map(r => r.tokenId),
      takes: Object.fromEntries(plan.rows.map(r => [r.tokenId, r.take]))
    });
  }, [srcJob, plan]);

  const skipByToken = useMemo(
    () => new Map(skips.map(s => [s.tokenId, s])),
    [skips]
  );

  // Acknowledged once, and re-asked whenever the set of skipped drums changes —
  // a tick carried over from a different set is not a decision about this one.
  const [skipsOk, setSkipsOk] = useState('');
  const skipsKey = skips.map(s => `${s.tokenId}:${s.reason}`).sort().join('|');
  const skipsAcknowledged = !skipsKey || skipsOk === skipsKey;

  const untickSkipped = () =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      for (const s of skips) next.delete(s.tokenId);
      return next;
    });

  const canSubmit =
    !!sourceJobId &&
    branchDrums.length > 0 &&
    targetValid &&
    isChange &&
    // Everything `problemsOf` calls blocking: a selection spanning two phases,
    // one mixing made and queued drums, or a plan that moves nothing.
    blocking.length === 0 &&
    // Refusing rather than guessing: a source that still owes more than it can
    // make is the exact state that made the order say 20,000 / 20,000 when half
    // the cable had gone to a different product.
    overPromised <= 0 &&
    // Drums that will be skipped are not a refusal — the transform still does
    // the right thing with the rest — but they must be SEEN first.
    skipsAcknowledged &&
    // …and at least one ticked drum has to actually be movable, or the fork
    // comes back with "no forkable links" and nothing has happened.
    skips.length < plan.rows.length &&
    !busy;

  // resolve the target standard (phases + materials) — shared by convert + fork.
  async function resolveTarget() {
    const payload = [
      {
        name: String(target.name).trim(),
        numberOfConductors: toNum(target.numberOfConductors),
        section: toNum(target.section),
        length: toNum(srcJob.plannedLength),
        color: String(target.color).trim(),
        hasMixedConductorSections: false,
        greenYellowSection: ''
      }
    ];
    const resp = await axios.post(
      `${API.BACKEND}/api/v1/standard/find-many`,
      payload
    );
    const standards = resp?.data?.data || [];
    const std = standards.find(
      s =>
        String(s.name).trim().toUpperCase() ===
          String(target.name).trim().toUpperCase() &&
        toNum(s.numberOfConductors) === toNum(target.numberOfConductors) &&
        toNum(s.section) === toNum(target.section) &&
        String(s.color || '').trim().toUpperCase() ===
          String(target.color).trim().toUpperCase()
    );
    if (!std) {
      throw new Error(
        `No standard found for ${formatProductLabel(target)} ${target.color}`
      );
    }
    const phases = (std.phases || []).map((phase, i, arr) => ({
      ...phase,
      dependsOn: i > 0 ? arr[i - 1].name : null
    }));
    return {
      standardId: std._id || std.standardId || null,
      name: std.name,
      numberOfConductors: toNum(std.numberOfConductors),
      section: toNum(std.section),
      color: std.color || target.color,
      phases
    };
  }

  const goPlanDownstream = rows =>
    navigate('/apps/project-management/plan-next-phase-wizard', {
      state: {
        from: 'TransformWizzard',
        tokenIds: rows.map(r => r.tokenId),
        rows,
        returnTo
      }
    });

  // The backend audits the product's four statements before it writes and hands
  // back anything that did not add up. Redirecting straight to the next wizard
  // would bury that — the planner is the only one who can act on it, so the
  // hand-off waits until they have seen it.
  const finish = (warnings, rows) => {
    if (Array.isArray(warnings) && warnings.length) {
      setResultWarnings(warnings);
      setPendingRows(rows || []);
      setBusy(false);
      return;
    }
    if (rows && rows.length) goPlanDownstream(rows);
    else navigate(returnTo);
  };

  // `convert-product` used to be called from here. It is not any more — see the
  // note at the top of this file. The endpoint still exists for eng-mobile.

  // PARTIAL — fork `forkLength` of the branch drums into a NEW target job; the
  // source keeps the remainder. Downstream planned on the new fork job.
  const handleFork = async () => {
    const tgt = targetStd || (await resolveTarget());
    const len = toNum(forkLength);

    if (branchIsPending) {
      // THE ONE PLACE PER-DRUM FIGURES ARE NOT HONOURED, and it is a backend
      // limit rather than a choice: `forkProductMerge` splits the whole lot by
      // a single share, `f = forkLength / sourceLength`, because a queued drum
      // has nothing on it to cut — it is re-planned as a merged pair sharing a
      // bobbin. So the per-drum boxes above are collapsed into `plan.buildable`
      // for this path, and the UI says so where the figures are typed.
      const forkResp = await http.post('/api/redis/jobs/fork-product-merge', {
        sourceJobId,
        // The default for any phase not named, then the per-phase choices.
        layout: layoutAll,
        layouts: Object.fromEntries(sharedPhases.map(ph => [ph, layoutOf(ph)])),
        branchPhase,
        target: tgt,
        forkLength: len,
        allocationDecisions
      });
      const data = forkResp?.data;
      if (!data?.forkJobId) throw new Error(data?.error || 'fork-merge failed');
      const rows = branchDrums.map(d => ({
        jobId: data.forkJobId,
        tokenId: d.tokenId,
        phase: branchPhase,
        color: d.color ?? null,
        plannedLength: len,
        availableLength: 0,
        producedLength: 0,
        status: 'PENDING',
        productLabel: data.productLabel || tgt.name
      }));
      finish(data.warnings, rows);
      return;
    }

    // PER-DRUM lengths. `fork-product` has always accepted them —
    // `links[].length` — and the screen simply used to send one figure for all
    // of them. The colours no longer have to agree; `problemsOf` warns when
    // they do not and says how long the result can actually be.
    const links = plan.rows.map(r => ({
      sourceTokenId: r.tokenId,
      length: r.take,
      color: r.color
    }));
    const forkResp = await http.post('/api/redis/jobs/fork-product', {
      sourceJobId,
      branchPhase,
      target: tgt,
      links,
      forkLength: len,
      allocationDecisions
    });
    const data = forkResp?.data;
    if (!data?.forkJobId) throw new Error(data?.error || 'fork failed');
    const rows = (data.injected || []).map(inj => ({
      jobId: data.forkJobId,
      tokenId: inj.tokenId,
      phase: branchPhase,
      color: inj.color ?? null,
      producedLength: inj.length,
      availableLength: inj.length,
      productLabel: data.productLabel || tgt.name
    }));
    finish(data.warnings, rows);
  };

  const handleSubmit = async () => {
    setSubmitErr('');
    setBusy(true);
    try {
      await handleFork();
    } catch (e) {
      setSubmitErr(e?.response?.data?.error || e?.message || String(e));
      setBusy(false);
    }
  };

  if (loadErr) {
    return (
      <Card className="mb-9 mt-3">
        <Card.Body>
          <h3 className="mb-2">Transform product</h3>
          <Alert variant="warning" className="py-2 mb-0">
            {loadErr}
          </Alert>
        </Card.Body>
      </Card>
    );
  }
  if (!srcJob) {
    return (
      <div className="text-center py-6">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const targetLabel = `${target.name || '—'} ${toNum(
    target.numberOfConductors
  )}x${toNum(target.section)}${target.color ? ` ${target.color}` : ''}`;

  return (
    <Card className="mb-9 mt-3">
      <Card.Header>
        <h3 className="mb-1">Transform product</h3>
        <div className="fs-9 text-700 d-flex align-items-center flex-wrap gap-2">
          Re-plan
          <span className="d-inline-flex align-items-center gap-1 fw-semibold">
            <ProductColorDot colorName={srcColor} />
            {srcJob.productLabel}
            {srcColor ? ` (${srcColor})` : ''}
          </span>
          into a different product. Executed phases and tasks are always
          preserved — a transform never throws away work that is already on a
          drum.
        </div>
      </Card.Header>
      <Card.Body>
        {mixedPhase && (
          <Alert variant="warning" className="py-2">
            The selection mixes phases — the downstream will be planned from{' '}
            <b>{PHASE_LABEL[branchPhase] || branchPhase}</b>.
          </Alert>
        )}
        {submitErr && (
          <Alert variant="danger" className="py-2">
            {submitErr}
          </Alert>
        )}

        {!!resultWarnings.length && (
          <Alert variant="warning" className="py-2">
            <div className="fw-semibold mb-1">
              The transform was applied, but the numbers do not all agree:
            </div>
            <ul className="mb-2 ps-3 fs-9">
              {resultWarnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
            <div className="fs-10 text-700 mb-2">
              Nothing is lost — this is the product&apos;s promise disagreeing
              with itself. Reconcile it in Sales, or run{' '}
              <code>scripts/resync-allocations.mjs</code>.
            </div>
            <Button
              size="sm"
              variant="primary"
              onClick={() =>
                pendingRows && pendingRows.length
                  ? goPlanDownstream(pendingRows)
                  : navigate(returnTo)
              }
            >
              {pendingRows && pendingRows.length
                ? 'Continue to plan downstream'
                : 'Back'}
            </Button>
          </Alert>
        )}

        {/* ── 1. the drums ────────────────────────────────── */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-1">
          <h5 className="mb-0">Drums to transform</h5>
          {/* Asked for 2026-10-30. OFF by default: the planner has just picked
              drums on the previous screen, and opening with the whole product
              ticked is how an accident becomes a lot-wide transform. */}
          <Form.Check
            type="switch"
            id="transform-all-phases"
            checked={allPhases}
            onChange={e => setAllPhases(e.target.checked)}
            label={
              <span className="fs-9">
                Bring every phase of {srcJob.name}
                <span className="text-700">
                  {" "}— {jobDrums.length} drum(s) across{" "}
                  {byPhase(jobDrums).length} phase(s)
                </span>
              </span>
            }
          />
        </div>
        <p className="text-700 fs-9 mb-2">
          {allPhases ? (
            <>
              Every drum of this product, in phase order. <b>One phase at a
              time</b> — ticking a drum locks the rest of the list to its
              phase, because a later phase consumes the drums of an earlier one.
            </>
          ) : (
            <>
              What you picked on Project Token State. Untick a drum to leave it
              on {srcJob.name}, or turn on the switch to see the whole product.
            </>
          )}{" "}
          Type how many metres leave each drum; the rest stays where it is.
          <b> Nothing that is not ticked is touched.</b>
        </p>
        <Table size="sm" className="fs-9 align-middle mb-2">
          <thead>
            <tr className="text-uppercase text-700 fs-10">
              <th style={{ width: 36 }} />
              <th>Token</th>
              <th>Phase</th>
              <th>Colour</th>
              <th className="text-end">Free</th>
              <th>Already planned</th>
              <th className="text-end" style={{ width: 130 }}>
                Convert (m)
              </th>
              <th className="text-end">Stays on {srcJob.name}</th>
              <th>Becomes</th>
            </tr>
          </thead>
          <tbody>
            {allDrums.map(d => {
              const on = selectedIds.has(d.tokenId);
              const pool = d.maxTake;
              const row = plan.rows.find(r => r.tokenId === d.tokenId) || null;
              const moving = row ? row.take : 0;
              const stays = Math.max(0, pool - moving);
              const skip = skipByToken.get(d.tokenId) || null;
              // Why this drum cannot be ticked: spent, a husk, or on a phase
              // other than the one the selection is locked to. Said per drum --
              // a greyed-out checkbox with no reason is where this screen was.
              const blocked = blockedReason(d, { locked, kind: d.kind });
              return (
                <tr
                  key={d.tokenId}
                  className={
                    on && skip ? 'table-warning' : on ? '' : 'opacity-50'
                  }
                >
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={on}
                      disabled={!!blocked}
                      title={blocked || undefined}
                      onChange={() => toggle(d.tokenId)}
                      aria-label={`Include ${d.tokenId}`}
                    />
                  </td>
                  <td className="font-monospace">
                    {d.tokenId}
                    {!d.wasHandedOver && (
                      <Badge bg="info" className="ms-2 fs-10">
                        other phase
                      </Badge>
                    )}
                  </td>
                  <td>
                    {PHASE_LABEL[d.phase] || d.phase}
                    {blocked && (
                      <div className="fs-10 text-warning" title={blocked}>
                        {d.kind === 'spent'
                          ? 'spent'
                          : d.kind === 'empty'
                            ? 'empty'
                            : 'other phase'}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="d-inline-flex align-items-center gap-2">
                      <ProductColorDot colorName={d.color} />
                      {d.color || '—'}
                    </span>
                  </td>
                  <td className="text-end">
                    {fmt(pool)} m
                    {d.isPending && (
                      <Badge bg="warning" className="ms-2 fs-10">
                        queued
                      </Badge>
                    )}
                  </td>

                  {/* Where this drum's metres already are. The fork refuses a
                      drum that is spoken for and used to say so only afterwards,
                      as "fork 15000 > effective 0". */}
                  <td>
                    {skip ? (
                      <span className="d-inline-flex flex-column">
                        <Badge bg="danger" className="fs-10 align-self-start">
                          ⛔{' '}
                          {skip.reason === 'forked'
                            ? 'FORKED'
                            : skip.reason === 'running'
                              ? 'ON A MACHINE'
                              : skip.reason === 'consumed'
                                ? 'CONSUMED'
                                : skip.reason === 'short'
                                  ? 'TOO SHORT'
                                  : 'PLANNED'}
                          {skip.reason === 'forked'
                            ? ` → ${skip.forkedTo
                                .map(f => f.productLabel || f.jobId)
                                .join(', ')}`
                            : skip.phases.length
                              ? ` → ${skip.phases
                                  .join(' + ')
                                  .replaceAll('_', ' ')}`
                              : ''}
                        </Badge>
                        <small className="text-danger">
                          {skip.reason === 'forked'
                            ? 'nothing left on it'
                            : (skip.running || []).length
                              ? `${(skip.running || [])
                                  .map(
                                    c =>
                                      `${String(c.machineId || '?').toUpperCase()} is drawing ` +
                                      `${fmt(c.metres)} m more`
                                  )
                                  .join('; ')} · ${fmt(skip.effective)} m free`
                              : `${fmt(skip.committed)} m spoken for · ${fmt(
                                  skip.effective
                                )} m free`}
                        </small>
                      </span>
                    ) : (
                      <span className="text-700">free</span>
                    )}
                  </td>

                  {/* ONE BOX PER DRUM. Empty is zero, never "all of it" --
                      `planTransform` clamps to what the drum holds and reports
                      the clamp rather than applying it silently. */}
                  <td className="text-end">
                    {!on ? (
                      <span className="text-700">—</span>
                    ) : (
                      <>
                        <Form.Control
                          size="sm"
                          type="number"
                          className="text-end"
                          value={takes[d.tokenId] ?? ''}
                          onChange={e => setTake(d.tokenId, e.target.value)}
                          placeholder={`≤ ${fmt(pool)}`}
                          isInvalid={!!skip}
                        />
                        {row?.clamped != null && (
                          <small className="text-warning">
                            capped from {fmt(row.clamped)}
                          </small>
                        )}
                        {skip && (
                          <small className="d-block text-danger">skipped</small>
                        )}
                      </>
                    )}
                  </td>
                  <td className="text-end">
                    {on && !skip ? `${fmt(stays)} m` : `${fmt(pool)} m`}
                  </td>
                  <td>
                    {on && !skip ? (
                      <span className="d-inline-flex align-items-center gap-2">
                        <ProductColorDot colorName={target.color} />
                        {targetLabel}
                      </span>
                    ) : (
                      <span className="text-700">stays {srcJob.name}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {!branchDrums.length && (
          <Alert variant="warning" className="py-2">
            No drum selected — tick at least one.
          </Alert>
        )}

        {/* The drums the fork will leave behind, said BEFORE it runs. This is
            the whole point of the preview: the backend's own refusal arrived
            afterwards as "fork 15000 > effective 0", which is true and tells
            nobody that the drum is on the rope's plan. */}
        {skips.length > 0 && (
          <Alert
            variant={skips.length >= branchDrums.length ? 'danger' : 'warning'}
            className="py-2 fs-9"
          >
            <div className="fw-bold mb-1">
              {skips.length} of {branchDrums.length} ticked drum
              {branchDrums.length === 1 ? '' : 's'} will NOT be transformed
              {skips.length >= branchDrums.length && ' — nothing would move'}
            </div>
            <ul className="mb-2 ps-3">
              {skips.map(s => (
                <li key={s.tokenId}>
                  <span className="font-monospace">{s.tokenId}</span>
                  {s.colour ? ` (${s.colour})` : ''} —{' '}
                  {skipReasonText(s, { sourceName: srcJob.name })}
                </li>
              ))}
            </ul>
            <div className="text-700 mb-2">
              Their metres are promised to a later phase, so they stay on{' '}
              {srcJob.name}. The rest of the ticked drums transform normally.
              Release the plan downstream first if they were meant to move.
            </div>
            <div className="d-flex flex-wrap align-items-center gap-3">
              <Button size="sm" variant="outline-secondary" onClick={untickSkipped}>
                Untick {skips.length === 1 ? 'it' : `all ${skips.length}`}
              </Button>
              {skips.length < branchDrums.length && (
                <Form.Check
                  type="checkbox"
                  id="ack-skips"
                  checked={skipsAcknowledged}
                  onChange={e => setSkipsOk(e.target.checked ? skipsKey : '')}
                  label="I understand these drums stay behind — go ahead with the others"
                />
              )}
            </div>
          </Alert>
        )}

        {/* ── 2. target ─────────────────────────────────────────────────── */}
        <h5 className="mb-2 mt-4">Target product</h5>
        <Row className="g-3 mb-2">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Target product</Form.Label>
              <Form.Select
                value={target.name}
                onChange={e => setTarget(t => ({ ...t, name: e.target.value }))}
              >
                <option value="">Select product…</option>
                {CABLE_NAMES_NO_ROPE.map(n => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Conductors</Form.Label>
              <Form.Control
                type="number"
                value={target.numberOfConductors}
                onChange={e =>
                  setTarget(t => ({ ...t, numberOfConductors: e.target.value }))
                }
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Section (mm²)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={target.section}
                onChange={e =>
                  setTarget(t => ({ ...t, section: e.target.value }))
                }
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Color</Form.Label>
              <Form.Select
                value={target.color}
                onChange={e => setTarget(t => ({ ...t, color: e.target.value }))}
              >
                <option value="">Select color…</option>
                {CABLE_COLORS.map(c => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* what those four fields actually add up to */}
        <div className="bg-body-emphasis border rounded-2 p-3 mb-3 fs-9">
          {stdBusy && (
            <span className="text-700">
              <Spinner animation="border" size="sm" className="me-2" />
              Resolving the target recipe…
            </span>
          )}
          {!stdBusy && stdErr && (
            <span className="text-danger">{stdErr}</span>
          )}
          {!stdBusy && !stdErr && !targetStd && (
            <span className="text-700">
              Pick product, conductors, section and colour to see the recipe.
            </span>
          )}
          {!stdBusy && targetStd && (
            <>
              <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                <ProductColorDot colorName={srcColor} />
                <span className="fw-semibold">
                  {formatProductLabel(srcJob)}
                  {srcColor ? ` ${srcColor}` : ''}
                </span>
                <span className="text-700 fs-8">→</span>
                <ProductColorDot colorName={targetStd.color} />
                <span className="fw-semibold">
                  {formatProductLabel(targetStd)} {targetStd.color}
                </span>
              </div>
              <div className="d-flex align-items-center flex-wrap gap-1">
                <span className="text-700 me-1">Recipe:</span>
                {(targetStd.phases || []).map(p => {
                  const nm = p.name || p.phase;
                  return (
                    <Badge
                      key={nm}
                      bg={nm === branchPhase ? 'primary' : 'secondary'}
                      variant="phoenix"
                      className="fs-10"
                    >
                      {PHASE_LABEL[nm] || nm}
                    </Badge>
                  );
                })}
              </div>
              {targetBranchColors && (
                <div className="mt-2 d-flex align-items-center flex-wrap gap-2">
                  <span className="text-700">
                    Needs at {PHASE_LABEL[branchPhase] || branchPhase}:
                  </span>
                  {targetBranchColors.map((c, i) => (
                    <span
                      key={`${c}-${i}`}
                      className="d-inline-flex align-items-center gap-1"
                    >
                      <ProductColorDot colorName={c} />
                      <span className="fs-10">{c}</span>
                    </span>
                  ))}
                  <Badge
                    bg={
                      branchDrums.length >= neededCount ? 'success' : 'warning'
                    }
                    variant="phoenix"
                    className="fs-10"
                  >
                    {branchDrums.length} of {neededCount} selected
                  </Badge>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── 3. how much ─────────────────────────────────── */}
        {/* The Total / Partial radios used to be here. Total converted the whole
            product in place and did not look at the drum ticks at all, which is
            what made it dangerous. There is nothing to
            choose any more: the figures typed per drum above ARE the answer. */}
        <Row className="g-3 mb-3 align-items-end">
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                Set every selected drum to (m)
              </Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="number"
                  value={setAllValue}
                  onChange={e => setSetAllValue(e.target.value)}
                  placeholder={maxFork > 0 ? `≤ ${fmt(maxFork)}` : ''}
                />
                <Button
                  variant="outline-secondary"
                  disabled={!plan.rows.length}
                  onClick={applySetAll}
                >
                  Apply
                </Button>
              </div>
              <Form.Text className="text-700">
                A convenience, not the figure itself — each drum keeps its own
                box above and can be changed after.
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={8}>
            <div className="fs-9 text-700">
              {plan.rows.length}{' '}
              <b>{PHASE_LABEL[plan.phase] || plan.phase || '—'}</b> drum(s)
              selected, {fmt(plan.totalTake)} m moving in total.
              {plan.parallel && plan.colourTotals.length > 1 && (
                <>
                  {' '}
                  The new product can be <b>{fmt(plan.buildable)} m</b> long —
                  as long as its shortest core.
                </>
              )}
            </div>
            {branchIsPending && (
              <div className="fs-10 text-info mt-1">
                These drums are still queued, so the split is re-planned rather
                than cut — see <b>On the machine</b> below for how the two halves
                are laid out. This path splits the lot by ONE share, so the
                per-drum figures are collapsed to {fmt(plan.buildable)} m.
              </div>
            )}
          </Col>
        </Row>

        {/* ── PREVIEW ──────────────────────────────────────── */}
        {/* Asked for 2026-10-30: "trebuie sa avem un preview sa vad vizual ce se
            va intampla cu conversia, sa-mi fac o idee."

            Built from the SAME `plan` object the submit posts, so it cannot
            describe something other than what will happen. It says three things
            and the third is the one that was missing: what is NOT touched. */}
        {plan.rows.length > 0 && (
          <Card className="mb-3 border-info-subtle">
            <Card.Header className="bg-info-subtle py-2 fs-9 fw-semibold">
              What this will do
            </Card.Header>
            <Card.Body className="py-2">
              <Row className="g-3">
                <Col md={6}>
                  <div className="fs-10 text-uppercase text-700 mb-1">
                    Leaves {srcJob.name}
                  </div>
                  <div className="fs-9">
                    {plan.rows.map(r => (
                      <div
                        key={r.tokenId}
                        className="d-flex align-items-center gap-2"
                      >
                        <ProductColorDot colorName={r.color} />
                        <span className="font-monospace fs-10">
                          {r.tokenId}
                        </span>
                        <span className="fw-semibold">{fmt(r.take)} m</span>
                        <span className="text-700">
                          of {fmt(r.maxTake)} m
                        </span>
                        {r.keep > 0 && (
                          <span className="text-success fs-10">
                            {fmt(r.keep)} m stays
                          </span>
                        )}
                        {r.keep <= 0 && (
                          <Badge bg="warning" className="fs-10">
                            drum emptied
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>

                  {plan.colourTotals.length > 1 && (
                    <div className="fs-10 text-700 mt-2">
                      Per colour:{" "}
                      {plan.colourTotals
                        .map(c => `${c.color} ${fmt(c.take)} m`)
                        .join(' · ')}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <div className="fs-10 text-uppercase text-700 mb-1">
                    Becomes
                  </div>
                  <div className="fs-9 d-flex align-items-center gap-2 mb-1">
                    <ProductColorDot colorName={target.color} />
                    <b>{targetLabel}</b>
                    <Badge bg="success">{fmt(plan.buildable)} m</Badge>
                    <Badge bg="secondary" variant="phoenix">
                      {PHASE_LABEL[plan.phase] || plan.phase}
                    </Badge>
                  </div>
                  <div className="fs-10 text-700">
                    {plan.path === 'merge' ? (
                      <>
                        {layoutOf(branchPhase) === 'merge' ? (
                          <>
                            Re-planned as a <b>merged pair sharing one bobbin</b>,
                            so the line rolls straight over from {srcJob.name}
                            {' '}onto {targetLabel}.
                          </>
                        ) : layoutOf(branchPhase) === 'byColor' ? (
                          <>
                            {targetLabel} gets <b>its own drums</b>, each queued
                            beside the same-colour {srcJob.name} drum.
                          </>
                        ) : (
                          <>
                            {targetLabel} gets <b>its own drums</b>, queued as one
                            block immediately after {srcJob.name}.
                          </>
                        )}{' '}
                        Either way the queue position is kept.
                      </>
                    ) : (
                      <>
                        The metres are cut off the produced drums and injected
                        into a new {targetLabel} job, which then has its
                        downstream phases planned.
                      </>
                    )}
                  </div>

                  {/* THE PART THAT WAS MISSING. TOTAL rewrote tasks that were
                      already DONE and nothing on screen said it would. */}
                  <div className="fs-10 text-uppercase text-700 mt-3 mb-1">
                    Not touched
                  </div>
                  <div className="fs-10 text-success">
                    {(() => {
                      const untouched = jobDrums.filter(
                        d => !selectedIds.has(d.tokenId)
                      );
                      if (!untouched.length) {
                        return 'Every drum of this product is in the selection.';
                      }
                      const spent = untouched.filter(
                        d => d.kind === 'spent' || d.kind === 'empty'
                      ).length;
                      return (
                        `${untouched.length} other drum(s) of ${srcJob.name} ` +
                        `stay exactly as they are` +
                        (spent
                          ? `, including ${spent} already finished or consumed.`
                          : '.')
                      );
                    })()}
                  </div>
                  <div className="fs-10 text-700">
                    {srcJob.name} keeps {fmt(capacityAfter)} m of its{' '}
                    {fmt(srcJob.plannedLength)} m.
                  </div>
                </Col>
              </Row>

              {/* Warnings, worst first. Blocking ones stop the button; the rest
                  are said and allowed — unequal colours is a real choice. */}
              {problems.length > 0 && (
                <div className="mt-3">
                  {problems.map((pr, i) => (
                    <div
                      key={i}
                      className={`fs-10 ${
                        pr.blocking ? 'text-danger fw-semibold' : 'text-warning'
                      }`}
                    >
                      {pr.blocking ? '⛔ ' : '⚠ '}
                      {pr.text}
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* ── how the halves are queued, ONE CHOICE PER PHASE ────────── */}
        {branchIsPending && plan.rows.length > 0 && sharedPhases.length > 0 && (
          <div className="mb-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
              <h6 className="mb-0">On the machine</h6>
              <div className="d-flex align-items-center gap-2">
                <span className="fs-10 text-700">Set all phases to</span>
                <Form.Select
                  size="sm"
                  style={{ width: 250 }}
                  value={layoutAll}
                  onChange={e => {
                    // Clears the overrides on purpose: "the same for all" has to
                    // BE visibly the same, not a default hiding behind three
                    // stale per-phase choices.
                    setLayoutAll(e.target.value);
                    setLayouts({});
                  }}
                >
                  {LAYOUT_OPTIONS.map(o => (
                    <option key={o.key} value={o.key}>
                      {o.title}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>

            <p className="text-700 fs-9 mb-2">
              These drums are still queued, so they can be laid out three ways
              — and the split reaches every phase up to{' '}
              <b>{PHASE_LABEL[branchPhase] || branchPhase}</b>, each of which can
              be laid out differently. Whichever is chosen, the queue POSITION is
              kept: the fork lands with the lot it came from, never at the end
              behind somebody else&apos;s work.
            </p>

            <Table size="sm" className="fs-9 align-middle mb-0">
              <thead>
                <tr className="text-uppercase text-700 fs-10">
                  <th>Phase</th>
                  <th style={{ width: 270 }}>How</th>
                  <th>What that means on the queue</th>
                </tr>
              </thead>
              <tbody>
                {sharedPhases.map(ph => {
                  const chosen = layoutOf(ph);
                  const opt = LAYOUT_OPTIONS.find(o => o.key === chosen);
                  const nothingQueued = !pendingPhases.has(ph);
                  return (
                    <tr key={ph} className={nothingQueued ? 'opacity-50' : ''}>
                      <td className="fw-semibold">
                        {PHASE_LABEL[ph] || String(ph).replace(/_/g, ' ')}
                        {ph === branchPhase && (
                          <Badge bg="info" className="ms-2 fs-10">
                            branch
                          </Badge>
                        )}
                      </td>
                      <td>
                        <Form.Select
                          size="sm"
                          value={chosen}
                          disabled={nothingQueued}
                          onChange={e =>
                            setLayouts(prev => ({
                              ...prev,
                              [ph]: e.target.value
                            }))
                          }
                        >
                          {LAYOUT_OPTIONS.map(o => (
                            <option key={o.key} value={o.key}>
                              {o.title}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td className="fs-10 text-700">
                        {nothingQueued
                          ? 'Already made — nothing of this phase is queued, so ' +
                            'there is nothing to lay out. Its metres are cut, not ' +
                            're-planned.'
                          : opt?.body(srcJob.name, targetLabel)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}

        {incompleteSelection && (
          <Alert variant="warning" className="py-2 fs-9">
            <b>
              {branchDrums.length} of {neededCount} conductors selected
            </b>{' '}
            — {targetLabel} needs all {neededCount}
            {missingColors.length ? (
              <>
                {' '}
                (missing{' '}
                {missingColors.map((c, i) => (
                  <span key={c} className="d-inline-flex align-items-center gap-1">
                    <ProductColorDot colorName={c} />
                    <b>{c}</b>
                    {i < missingColors.length - 1 ? ', ' : ''}
                  </span>
                ))}
                )
              </>
            ) : null}
            . The new product will own only the drums ticked above, so its{' '}
            <b>rope cannot be planned</b> until the missing conductors are
            forked into it as well — and there is no way to add them afterwards.
            Fork all {neededCount} together unless you specifically want a
            single-conductor lot.
          </Alert>
        )}

        {/* ── 4. orders ─────────────────────────────────────────────────── */}
        <h5 className="mb-1 mt-4">Orders &amp; reservations</h5>
        {allocBusy && (
          <p className="text-700 fs-9">
            <Spinner animation="border" size="sm" className="me-2" />
            Looking up what this product is promised to…
          </p>
        )}
        {allocErr && (
          <Alert variant="danger" className="py-2 fs-9">
            Could not read the allocations: {allocErr}. Transform anyway and the
            orders stay exactly as they are.
          </Alert>
        )}
        {!allocBusy && !allocErr && !allocLines.length && (
          <p className="text-700 fs-9">
            Not allocated to any order — this is stock, so there is nothing to
            decide.
          </p>
        )}
        {!!allocLines.length && (
          <>
            <p className="text-700 fs-9 mb-2">
              {`${fmt(
                toNum(forkLength)
              )} m leave this product and start as STOCK — a different cable cannot fulfil a line that asked for this one. What is decided here is how much of its promise ${
                srcJob.name
              } keeps.`}
            </p>

              <Alert
                variant={overPromised > 0 ? 'danger' : 'success'}
                className="py-2 fs-9"
              >
                <b>{srcJob.name}</b> can make <b>{fmt(capacityAfter)} m</b> after
                this fork, and still promises <b>{fmt(promisedAfter)} m</b>.
                {overPromised > 0 ? (
                  <>
                    {' '}
                    Over by <b>{fmt(overPromised)} m</b> — trim below before this
                    can be submitted.
                    <Button
                      size="sm"
                      variant="phoenix-secondary"
                      className="ms-2"
                      onClick={autoTrim}
                    >
                      Auto-trim to capacity
                    </Button>
                  </>
                ) : (
                  ' That fits.'
                )}
              </Alert>
            

            {allocLines.map(l => {
              const k = `${l.orderId}::${l.lineId}`;
              const d = decisions[k] || { action: 'KEEP', keep: {}, reduceMeters: 0 };
              const owed = toNum(l.allocatedFromThisJob);
              const items = l.deliveryItems || [];
              const itemised = toNum(l.reservedUnits) > 0;
              const keptMeters = items.reduce(
                (s, it) =>
                  s + toNum((d.keep || {})[it.itemId]) * toNum(it.lengthPerUnit),
                0
              );
              return (
                <div key={k} className="border rounded-2 p-3 mb-2">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <Badge
                      bg="primary"
                      variant="phoenix"
                      className="fs-10 font-monospace"
                    >
                      {l.orderCode || l.orderId}
                    </Badge>
                    <span className="fw-semibold">{l.client || '—'}</span>
                    <span className="text-700 fs-10">{l.productLabel || '—'}</span>
                    {l.dueDate && (
                      <span className="text-700 fs-10">
                        due {String(l.dueDate).slice(0, 10)}
                      </span>
                    )}
                    <span className="ms-auto fs-9">
                      <b>{fmt(owed)} m</b>{' '}
                      <span className="text-700">promised by this product</span>
                      {itemised && (
                        <span className="text-700">
                          {' '}· {l.reservedUnits} unit(s) itemised
                        </span>
                      )}
                      {l.reserved && (
                        <Badge bg="info" variant="phoenix" className="ms-2 fs-10">
                          reserved
                        </Badge>
                      )}
                    </span>
                  </div>

                  {(l.orderMissing || l.lineMissing) && (
                    <Alert variant="warning" className="py-1 px-2 fs-10 mb-2">
                      {l.orderMissing
                        ? 'This order no longer exists'
                        : 'This line is no longer on the order'}{' '}
                      — the allocation is stale. Releasing it is the only thing
                      that will do anything.
                    </Alert>
                  )}

                  {itemised ? (
                    <>
                      <div className="text-700 fs-10 mb-1">
                        How many of each stay with {srcJob.name}:
                      </div>
                      <Table size="sm" className="fs-10 align-middle mb-1">
                        <thead>
                          <tr className="text-uppercase text-700">
                            <th>Delivery item</th>
                            <th className="text-end">Each</th>
                            <th className="text-end">Promised</th>
                            <th className="text-end" style={{ width: 110 }}>
                              Keeps
                            </th>
                            <th className="text-end">Released</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map(it => {
                            const have = toNum(it.reserved);
                            const kept = toNum((d.keep || {})[it.itemId]);
                            const label =
                              String(it.kind).toUpperCase() === 'COIL'
                                ? 'coils'
                                : `${it.drumMaterial || 'drum'}${it.drumDiameter ? ` Ø${it.drumDiameter}` : ''}`;
                            return (
                              <tr key={it.itemId}>
                                <td>{label}</td>
                                <td className="text-end">
                                  {fmt(it.lengthPerUnit)} m
                                </td>
                                <td className="text-end">{have}</td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    className="text-end"
                                    value={kept}
                                    max={have}
                                    onChange={e => {
                                      let v = Math.floor(toNum(e.target.value));
                                      if (v < 0) v = 0;
                                      if (v > have) v = have;
                                      setDecision(k, {
                                        keep: { ...(d.keep || {}), [it.itemId]: v }
                                      });
                                    }}
                                  />
                                </td>
                                <td className="text-end">
                                  {have - kept > 0 ? (
                                    <span className="text-warning fw-semibold">
                                      {have - kept}
                                    </span>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                      <div className="fs-10 text-700">
                        Keeps <b>{fmt(keptMeters)} m</b> of the {fmt(owed)} m it
                        owed;{' '}
                        <b>{fmt(Math.max(0, owed - keptMeters))} m</b> go back to
                        the order.
                      </div>
                    </>
                  ) : (
                    <Row className="g-2 align-items-end">
                      <Col md={3}>
                        <Form.Label className="fs-10 mb-1">
                          Metres to hand back
                        </Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          value={d.reduceMeters}
                          max={owed}
                          onChange={e => {
                            let v = toNum(e.target.value);
                            if (v < 0) v = 0;
                            if (v > owed) v = owed;
                            setDecision(k, { reduceMeters: v });
                          }}
                        />
                      </Col>
                      <Col className="fs-10 text-700">
                        No drums or coils are itemised on this line yet — its
                        split is still decided at the final phase, so only the
                        metres can move. Keeps{' '}
                        <b>{fmt(Math.max(0, owed - toNum(d.reduceMeters)))} m</b>.
                      </Col>
                    </Row>
                  )}
                </div>
              );
            })}
            {!allocationDecisions.length && (
              <div className="text-700 fs-10 mb-2">
                Nothing decided — the orders will be left exactly as they are.
              </div>
            )}
          </>
        )}

        <div className="d-flex gap-2">
          <Button variant="phoenix-secondary" onClick={() => navigate(returnTo)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!canSubmit} onClick={handleSubmit}>
            {busy ? 'Working…' : 'Split / Fork → plan downstream'}
          </Button>
        </div>
        <div className="text-700 fs-10 mt-2">
          Copper stays counted once (same conductors); the target recalculates
          its own sheath / armour.
        </div>
      </Card.Body>
    </Card>
  );
};

export default TransformWizzard;
